import { EditOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Card, Empty, Flex, Tree } from 'antd'
import { ItemType } from 'antd/es/menu/interface'
import { DataNode } from 'antd/es/tree'
import { Theme, useTheme } from 'antd-style'
import { Key, useEffect, useMemo, useState } from 'react'

import {
  useInstitutionServiceDeleteInstitutionEndpoint,
  useInstitutionServiceGetInstitutionHierarchyEndpoint,
  useInstitutionServiceGetInstitutionHierarchyEndpointKey
} from 'src/api/queries'
import {
  ApiError,
  GetInstitutionHierarchyResponse,
  Permissions
} from 'src/api/requests'
import { ArrowDown } from 'src/assets'
import {
  DeleteConfirm,
  message,
  TableActionButtons,
  Text
} from 'src/components'
import { useUser } from 'src/hooks'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler } from 'src/utils'

import { OrganizationalHeader } from '../OrganizationalHeader'

import { useStyles } from './styles'

type Props = DataNode & GetInstitutionHierarchyResponse

type TreeNodeCardProps = {
  node: Props
  isExpanded?: boolean
  hasChildren?: boolean
  getItems: (id?: string, length?: number) => ItemType[]
  theme: Theme
}

const TreeNodeCard = ({
  node,
  isExpanded,
  hasChildren,
  getItems,
  theme
}: TreeNodeCardProps) => {
  const { styles } = useStyles()

  const cardStyle = useMemo(() => {
    return {
      backgroundColor: hasChildren
        ? `${theme.grey.grey3}4D`
        : theme.lightGrey.lightGrey3
    }
  }, [hasChildren, theme])

  return (
    <Card style={cardStyle} className={styles.card} size="small">
      <Flex gap={16}>
        {hasChildren && (
          <Flex
            align="center"
            style={{
              transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'
            }}
          >
            <ArrowDown />
          </Flex>
        )}
        <Text>{node?.name}</Text>
      </Flex>
      <TableActionButtons items={getItems(node?.id)} />
    </Card>
  )
}

export type Filters = {
  parentId?: string
}

export const OrganizationalTree = () => {
  const [filters, setFilters] = useState<Filters>({})

  const { hasPermission } = useUser()
  const { styles } = useStyles()
  const { t } = useLocales()
  const theme = useTheme()

  const [expandedKeys, setExpandedKeys] = useState<Key[]>(['0'])

  const queryClient = useQueryClient()

  const { data, isLoading, isError } =
    useInstitutionServiceGetInstitutionHierarchyEndpoint(filters)

  const onExpand = (expandedKeysValue: Key[]) => {
    setExpandedKeys(expandedKeysValue)
  }

  const normalizeData = (node: Props): Props => ({
    ...node,
    key: node?.parentId === null ? `0` : (node?.id ?? ''),
    children: node?.children?.map(normalizeData) || []
  })

  const normalizedData = normalizeData(data as Props)

  const { mutate: deleteInstitution } =
    useInstitutionServiceDeleteInstitutionEndpoint({
      onSuccess: () => {
        message.success(
          t('organizational_structure.delete_institution_success')
        )
        queryClient.invalidateQueries({
          queryKey: [useInstitutionServiceGetInstitutionHierarchyEndpointKey]
        })
      },
      onError: (error: ApiError) => formErrorHandler(error.body.errors)
    })

  const getItems = useMemo(
    () => (id?: string, length?: number) => {
      return [
        {
          key: 'edit',
          label: (
            <a href={paths.organizationalStructure.details(String(id))}>
              {t('common.edit')}
            </a>
          ),
          icon: (
            <a href={paths.organizationalStructure.details(String(id))}>
              <EditOutlined />
            </a>
          )
        },
        ...(!length &&
        hasPermission(
          Permissions.ORGANIZATIONAL_STRUCTURE_ORGANIZATIONAL_STRUCTURE_DELETE_ALL
        )
          ? [
              {
                key: 'delete',
                danger: true,
                label: (
                  <DeleteConfirm
                    title={t('common.delete')}
                    onConfirm={() => deleteInstitution({ id: String(id) })}
                  >
                    <span>{t('common.delete')}</span>
                  </DeleteConfirm>
                )
              }
            ]
          : [])
      ]
    },
    [t]
  )

  useEffect(() => {
    if (!filters.parentId) {
      setExpandedKeys(['0'])
    }
  }, [filters.parentId, setExpandedKeys])

  return (
    <>
      <OrganizationalHeader setFilters={setFilters} />
      <Card loading={isLoading && !isError}>
        {data ? (
          <Tree
            treeData={[normalizedData]}
            titleRender={(node) => (
              <TreeNodeCard
                node={node}
                isExpanded={expandedKeys?.includes(node?.key)}
                hasChildren={node?.children && node?.children?.length > 0}
                getItems={() => getItems(node?.id, node?.children?.length)}
                theme={theme}
              />
            )}
            className={styles.treeNode}
            expandedKeys={expandedKeys}
            expandAction="click"
            onExpand={onExpand}
            blockNode
            showLine
          />
        ) : (
          <Empty />
        )}
      </Card>
    </>
  )
}
